import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import ToastNotification from "components/common/ToastNotification";
import { transformErrors } from "helpers/utils";
import {
  Col,
  Form,
  Row,
  Card,
  Table,
  InputGroup,
  Badge,
  Spinner,
  Modal,
} from "react-bootstrap";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFieldArray,
} from "react-hook-form";
import { Navigate, useNavigate, useParams, useBlocker } from "react-router-dom";
import {
  PromocaoRequest,
  promocaoBreadcrumbItems,
  ProdutoPromo,
} from "data/promocoes";
import { ResultResponse } from "data/produto";
import DatePicker from "components/base/DatePicker";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import {
  faPlus,
  faTrash,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import LojasAssociadasPromocao from "../promocoes/LojasAssociadasPromocao";
import SelecaoItensOffcanvas from "../promocoes/selecionarItensOffCanvas";
import { usePermissionModal } from "contexts/PermissionModalContext";

interface EditarPromocaoDtoType {
  codigo?: number;
  descricao: string;
  inativo: boolean;
  tipoDesconto: number;
  dataInicio?: Date;
  dataFim?: Date;
  ilimitada: boolean;
  acumulativa: boolean;
  pagueLeve: { pague: number; leve: number };
  descontoMultiplo: { quantidade: number; desconto: number }[];
  aplicarPara: number;
  produtoPromocoes: any[];
  empresasAssociadas: number[];
}

// Usando o tipo ResultResponse importado de data/produto.ts

const EditarPromocao = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const [showModalItens, setShowModalItens] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  // Use the permission modal hook
  const { showPermissionModal, getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();

  const isDirtyRef = useRef(false);

  const { isLoading, isError, data, error } = useQuery<
    ResultResponse<EditarPromocaoDtoType>
  >({
    queryKey: ["promocao", id],
    queryFn: async () => {
      const response = await axios.get(`/api/promocao/${id}`);
    
      return response.data;
    },
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: ["promocao", id],
      defaultMessage: "Você não possui permissão para consultar ou editar esta promoção."
    }),
    // Make sure the query is correctly invalidated when it fails
    staleTime: 0,
  });
  
  // Process the promotion data to handle dates correctly
  const promocao = data?.data ? {
    ...data.data,
    dataInicio: data.data.dataInicio ? new Date(data.data.dataInicio) : undefined,
    dataFim: data.data.dataFim ? new Date(data.data.dataFim) : undefined
  } : undefined;

  // Reimplement the form using the approach from EditarUsuario
  const form = useForm<EditarPromocaoDtoType>({
    values: promocao
      ? {
          ...promocao,
          // Ensure dates are properly formatted as Date objects
          dataInicio: promocao.dataInicio ? new Date(promocao.dataInicio) : undefined,
          dataFim: promocao.dataFim ? new Date(promocao.dataFim) : undefined,
        }
      : {
          codigo: undefined,
          descricao: "",
          inativo: false,
          tipoDesconto: 2,
          dataInicio: undefined,
          dataFim: undefined,
          ilimitada: false,
          acumulativa: false,
          pagueLeve: { pague: 1, leve: 2 },
          descontoMultiplo: [{ quantidade: 2, desconto: 10 }],
          aplicarPara: 1,
          produtoPromocoes: [],
          empresasAssociadas: [],
        },
  });

  const mutation = useMutation<any, any, PromocaoRequest>({
    mutationFn: (data) => {
      // Limpar e formatar os dados antes de enviar
      const promocaoData: any = {
        ...data,
        codigo: data.codigo,
        descricao: data.descricao,
        inativo: data.inativo || false,
        tipoDesconto: data.tipoDesconto,
        ilimitada: data.ilimitada || false,
        acumulativa: data.acumulativa || false,
        // Formatar datas corretamente
        dataInicio: data.ilimitada
          ? null
          : formatDateForBackend(data.dataInicio),
        dataFim: data.ilimitada ? null : formatDateForBackend(data.dataFim),
        // Garantir que os objetos estejam no formato correto
        pagueLeve:
          data.tipoDesconto === 1 ? data.pagueLeve : { pague: 1, leve: 2 },
        descontoMultiplo: data.tipoDesconto === 2 ? data.descontoMultiplo : [],
        aplicarPara: data.aplicarPara,
        // Limpar os dados dos produtos para enviar apenas o que é necessário
        produtoPromocoes: data.produtoPromocoes
          ? data.produtoPromocoes.map((item: any) => {
              return {
                produtoId:
                  (item.produtoId ?? 0) > 0 ? item.produtoId : undefined,
                departamentoId:
                  (item.departamentoId ?? 0) > 0
                    ? item.departamentoId
                    : undefined,
                categoriaId:
                  (item.categoriaId ?? 0) > 0 ? item.categoriaId : undefined,
              };
            })
          : [],
        lojasSelecionadas: data.empresasAssociadas || [],
      };
    
      return axios.put(`/api/promocao/${id}`, promocaoData);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar promoções.",
      (error) => {      

        // Verificar se há erros de validação do servidor
        if (error?.response?.data?.errors) {
          const formattedErrors = transformErrors(error.response.data.errors);
          for (const [field, error] of Object.entries(formattedErrors)) {
            form.setError(field as any, error as any);
          }
          setErrorMessage("Verifique os erros no formulário.");
          setShowErrorToast(true);
        }
        // Verificar se há uma mensagem de erro específica
        else if (error?.response?.data?.message) {
          setErrorMessage(error.response.data.message);
          setShowErrorToast(true);
        }
        // Erro genérico
        else {
          setErrorMessage(
            "Ocorreu um erro ao atualizar a promoção. Tente novamente."
          );
          setShowErrorToast(true);
        }
      }
    ),
    onSuccess: (response) => {   

      if (response?.data?.isSuccess) {
        // Reset the form's dirty state
        setFormDirty(false);
        isDirtyRef.current = false;

        // Navigate to the listing page with success message
        const codigo = form.watch("codigo") || "";
        navigate(`/cadastros/promocoes?successCode=${codigo}&new=false`);
      } else {
        setErrorMessage(
          response?.data?.message || "Ocorreu um erro ao atualizar a promoção."
        );
        setShowErrorToast(true);
      }
    },
  });

  useEffect(() => {
    if (!mutation.isPending) {
      // If the mutation was successful, ensure the form is not marked as dirty
      if (mutation.isSuccess) {
        isDirtyRef.current = false;
      }
    }
  }, [mutation.isPending, mutation.isSuccess]);

  // Track form dirty state in sync with the form's own isDirty state
  useEffect(() => {
    isDirtyRef.current = form.formState.isDirty;

    if (form.formState.isDirty && !formDirty) {
      setFormDirty(true);
    }
  }, [form.formState.isDirty, formDirty]);

  useEffect(() => {
    isDirtyRef.current = form.formState.isDirty;
  }, [form.formState.isDirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDirtyRef.current) return false;
    if (form.formState.isSubmitting) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    setPendingNavigation(nextLocation.pathname);
    setShowNavigationModal(true);

    return true;
  });

  // Estado para armazenar a navegação pendente
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );

  // Estado para controlar a exibição do modal de navegação
  const [showNavigationModal, setShowNavigationModal] = useState(false);

  // Função para lidar com a confirmação de navegação
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      // Resetar o estado de sujeira antes de navegar
      setFormDirty(false);
      isDirtyRef.current = false;

      // Se estamos usando blocker, temos que continuar com ele
      if (blocker.state === "blocked") {
        blocker.proceed?.();
      } else {
        // Caso contrário, é uma navegação manual (ex: botão Cancelar)
        navigate(pendingNavigation);
      }
    }
  };

  // Função para cancelar a navegação
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
    if (blocker.state === "blocked") {
      blocker.reset?.();
    }
  };

  const formatDateForBackend = (date: any) => {
    if (!date) return null;
    try {
      // Verificar se é uma data válida
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return null;
      }
      return dateObj.toISOString();
    } catch (error) {
      return null;
    }
  };

  const formatDateForPicker = (date: Date | string | undefined) => {
    if (!date) return undefined;
    try {
      // Handle both string dates and Date objects
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {      
        return undefined;
      }
      return dateObj;
    } catch (error) {   
      return undefined;
    }
  };

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "descontoMultiplo",
  });

  const { fields: produtoFields, remove: removeProduto } = useFieldArray({
    control: form.control,
    name: "produtoPromocoes",
  });

  const tipoDesconto = form.watch("tipoDesconto");
  const aplicarPara = parseInt(form.watch("aplicarPara") as any);
  const produtoPromocoes = form.watch("produtoPromocoes") || [];
  const empresasAssociadas = form.watch("empresasAssociadas") || [];
  

  const getItemType = (
    item: ProdutoPromo
  ): "produto" | "departamento" | "categoria" => {
    if ((item.produtoId ?? 0) > 0) return "produto";
    if ((item.departamentoId ?? 0) > 0) return "departamento";
    if ((item.categoriaId ?? 0) > 0) return "categoria";
    return "produto";
  };

  const getItemsByType = (type: "produto" | "departamento" | "categoria") => {
    return produtoPromocoes.filter((item) => getItemType(item) === type);
  };

  const getFilteredItems = () => {
    const itemType =
      aplicarPara === 1
        ? "produto"
        : aplicarPara === 2
        ? "departamento"
        : "categoria";
    return produtoPromocoes.filter((item) => getItemType(item) === itemType);
  };

  const handleRemoverItem = (index: number) => {
    removeProduto(index);
  };

  const handleToggleLoja = (lojaId: number) => {
    const updatedLojas = empresasAssociadas.includes(lojaId)
      ? empresasAssociadas.filter((id) => id !== lojaId)
      : [...empresasAssociadas, lojaId];

    // Standard setValue call - Updated with shouldDirty: true
    form.setValue("empresasAssociadas", updatedLojas, { shouldDirty: true });
  };

  const handleTipoDescontoChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = parseInt(e.target.value);

    const currentPagueLeve = form.getValues("pagueLeve");
    const currentDescontoMultiplo = form.getValues("descontoMultiplo");

    form.setValue("tipoDesconto", newValue, { shouldDirty: true });

    form.setValue("pagueLeve", currentPagueLeve, { shouldDirty: true });
    form.setValue(
      "descontoMultiplo",
      currentDescontoMultiplo.length > 0
        ? currentDescontoMultiplo
        : [{ quantidade: 2, desconto: 10 }],
      { shouldDirty: true }
    );
  };

  // Removemos os useEffects redundantes que estavam causando problemas
  // A lógica de sucesso e erro já está sendo tratada nos callbacks onSuccess e onError

  const onSubmit: SubmitHandler<any> = async (data) => {
    let itemType: "produto" | "departamento" | "categoria";
    let errorText: string;

    switch (data.aplicarPara) {
      case 1:
        itemType = "produto";
        errorText = "produto";
        break;
      case 2:
        itemType = "departamento";
        errorText = "departamento";
        break;
      case 3:
        itemType = "categoria";
        errorText = "categoria";
        break;
      default:
        itemType = "produto";
        errorText = "item";
    }

    if (getItemsByType(itemType).length === 0) {
      setErrorMessage(`Selecione ao menos um ${errorText}.`);
      setShowErrorToast(true);
      return;
    }

    if (!data.ilimitada) {
      if (!data.dataInicio || !data.dataFim) {
        setErrorMessage("Período de vigência deve ter data inicial e final.");
        setShowErrorToast(true);
        return;
      }

      if (new Date(data.dataInicio) > new Date(data.dataFim)) {
        setErrorMessage("A data final deve ser posterior à data inicial.");
        setShowErrorToast(true);
        return;
      }
    }

    if (
      data.tipoDesconto === 2 &&
      (!data.descontoMultiplo || data.descontoMultiplo.length === 0)
    ) {
      setErrorMessage(
        "Promoção do tipo Desconto Múltiplo deve ter pelo menos uma faixa configurada."
      );
      setShowErrorToast(true);
      return;
    }

    setFormDirty(false);
    isDirtyRef.current = false;

    mutation.mutate(data);
  };

  const handleAdicionarDescontoMultiplo = () => {
    const ultimaQtd =
      fields.length > 0 ? fields[fields.length - 1].quantidade + 1 : 2;
    append({ quantidade: ultimaQtd, desconto: 10 });
  };

  if (mutation.isSuccess && mutation.data?.data?.isSuccess !== false) {
    const codigo = form.watch("codigo") || "";
    return (
      <Navigate to={`/cadastros/promocoes?successCode=${codigo}&new=false`} />
    );
  }

  if (isLoading) {
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }  

  // Display error message if isSuccess is false
  if (data && data.isSuccess === false) {
    return (
      <div className="alert alert-danger">
        Erro ao carregar promoção:{" "}
        {data.message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  if (!promocao) {
    return <div>Promoção não encontrada</div>;
  }

  return (
    <div>
      <FormProvider {...form}>
        {/* Add UnsavedChangesModal for navigation confirmation */}
        <UnsavedChangesModal
          show={showNavigationModal}
          onConfirm={handleNavigationConfirm}
          onCancel={handleNavigationCancel}
        />

        <PageBreadcrumb items={promocaoBreadcrumbItems(form.watch("codigo"))} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-2">Editar uma promoção</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações da promoção
              </h5>
            </div>
            <Button
              variant="primary"
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar Alterações
            </Button>
          </div>

          <ToastNotification
            show={showErrorToast}
            type="error"
            message={errorMessage}
            onClose={() => setShowErrorToast(false)}
          />

          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">Informações Básicas</h5>
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Group controlId="descricao">
                    <Form.Label>
                      Descrição <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      {...form.register("descricao", {
                        required: "A descrição é obrigatória",
                      })}
                      placeholder="Ex: Promoção de Páscoa 2023"
                      isInvalid={!!form.formState.errors.descricao}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.descricao?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="tipoDesconto">
                    <Form.Label>Tipo de Desconto</Form.Label>
                    <Form.Select
                      value={form.watch("tipoDesconto")}
                      onChange={handleTipoDescontoChange}
                    >
                      <option value={2}>Desconto Múltiplo</option>
                      <option value={1}>Pague e Leve</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="aplicarPara">
                    <Form.Label>Aplicar Para</Form.Label>
                    <Form.Select {...form.register("aplicarPara")}>
                      <option value={1}>Produtos</option>
                      <option value={2}>Departamentos</option>
                      <option value={3}>Categorias</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Check
                    type="switch"
                    id="acumulativa"
                    label="Permitir combinar com outras promoções"
                    {...form.register("acumulativa")}
                  />
                </Col>
                <Col>
                  <Form.Check
                    type="switch"
                    id="inativo"
                    label="Inativo"
                    {...form.register("inativo")}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">Período de Vigência</h5>
              <div className="mb-3">
                <Form.Group>
                  <div className="mb-3">
                    <Form.Check
                      inline
                      type="radio"
                      id="vigenciaPeriodo"
                      label="Por Período"
                      checked={form.watch("ilimitada") === false}
                      onChange={() => {
                        form.setValue("ilimitada", false, {
                          shouldDirty: true,
                        });
                      }}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="vigenciaIlimitada"
                      label="Ilimitada"
                      checked={form.watch("ilimitada") === true}
                      onChange={() => {
                        form.setValue("ilimitada", true, { shouldDirty: true });
                      }}
                    />
                  </div>
                </Form.Group>
              </div>

              {form.watch("ilimitada") === false && (
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Data de Início <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className={
                          form.formState.errors.dataInicio ? "is-invalid" : ""
                        }
                      >
                        <DatePicker
                          name="dataInicio"
                          onChange={(date) =>
                            form.setValue(
                              "dataInicio",
                              Array.isArray(date) ? date[0] : date,
                              { shouldDirty: true }
                            )
                          }
                          value={form.watch("dataInicio")}
                          options={{
                            dateFormat: "d/m/Y",
                            enableTime: false,
                            locale: {
                              firstDayOfWeek: 0
                            }
                          }}
                        />
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          !!form.formState.errors.dataInicio ? "d-block" : ""
                        }
                      >
                        {form.formState.errors.dataInicio?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Data de Fim <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className={
                          form.formState.errors.dataFim ? "is-invalid" : ""
                        }
                      >
                        <DatePicker
                          name="dataFim"
                          onChange={(date) =>
                            form.setValue(
                              "dataFim",
                              Array.isArray(date) ? date[0] : date,
                              { shouldDirty: true }
                            )
                          }
                          value={form.watch("dataFim")}
                          options={{
                            dateFormat: "d/m/Y",
                            enableTime: false,
                            locale: {
                              firstDayOfWeek: 0
                            }
                          }}
                        />
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          !!form.formState.errors.dataFim ? "d-block" : ""
                        }
                      >
                        {form.formState.errors.dataFim?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {tipoDesconto === 1 && (
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">Configuração de Pague e Leve</h5>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Pague (quantidade){" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        {...form.register("pagueLeve.pague", {
                          required: "Quantidade é obrigatória",
                          min: {
                            value: 1,
                            message: "Quantidade deve ser pelo menos 1",
                          },
                          valueAsNumber: true,
                        })}
                        isInvalid={!!form.formState.errors.pagueLeve?.pague}
                      />
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.pagueLeve?.pague?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Leve (quantidade) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        {...form.register("pagueLeve.leve", {
                          required: "Quantidade é obrigatória",
                          min: {
                            value: 1,
                            message: "Quantidade deve ser pelo menos 1",
                          },
                          valueAsNumber: true,
                        })}
                        isInvalid={!!form.formState.errors.pagueLeve?.leve}
                      />
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.pagueLeve?.leve?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {tipoDesconto === 2 && (
            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Configuração de Desconto Múltiplo</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAdicionarDescontoMultiplo}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Adicionar Faixa
                  </Button>
                </div>

                <Table responsive>
                  <thead>
                    <tr>
                      <th>Quantidade</th>
                      <th>Desconto (%)</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => (
                      <tr key={field.id}>
                        <td>
                          <Form.Control
                            type="number"
                            min="1"
                            step="1"
                            {...form.register(
                              `descontoMultiplo.${index}.quantidade`,
                              {
                                required: "Obrigatório",
                                min: {
                                  value: 1,
                                  message: "Min: 1",
                                },
                                valueAsNumber: true,
                              }
                            )}
                            style={{ width: "100px" }}
                            isInvalid={
                              !!form.formState.errors.descontoMultiplo?.[index]
                                ?.quantidade
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {
                              form.formState.errors.descontoMultiplo?.[index]
                                ?.quantidade?.message
                            }
                          </Form.Control.Feedback>
                        </td>
                        <td>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              {...form.register(
                                `descontoMultiplo.${index}.desconto`,
                                {
                                  required: "Obrigatório",
                                  min: {
                                    value: 0,
                                    message: "Min: 0",
                                  },
                                  max: {
                                    value: 100,
                                    message: "Max: 100",
                                  },
                                  valueAsNumber: true,
                                }
                              )}
                              style={{ width: "100px" }}
                              isInvalid={
                                !!form.formState.errors.descontoMultiplo?.[
                                  index
                                ]?.desconto
                              }
                            />
                            <InputGroup.Text>%</InputGroup.Text>
                            <Form.Control.Feedback type="invalid">
                              {
                                form.formState.errors.descontoMultiplo?.[index]
                                  ?.desconto?.message
                              }
                            </Form.Control.Feedback>
                          </InputGroup>
                        </td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => remove(index)}
                            disabled={fields.length === 1}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Itens da Promoção</h5>
                <Button
                  variant="primary"
                  onClick={() => setShowModalItens(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Adicionar Itens
                </Button>
              </div>

              {produtoPromocoes.length === 0 ? (
                <div>
                  <p className="text-muted">
                    Nenhum item adicionado à promoção.
                  </p>
                </div>
              ) : (
                <>
                  <p className="mb-2">
                    {aplicarPara === 1
                      ? "Produtos "
                      : aplicarPara === 2
                      ? "Departamentos "
                      : "Categorias "}
                    selecionados ({getFilteredItems().length}):
                  </p>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {getFilteredItems().map((item, index) => {
                      const tipo = getItemType(item);
                      const textColor =
                        tipo === "produto"
                          ? "primary"
                          : tipo === "departamento"
                          ? "success"
                          : "info";

                      const originalIndex = produtoPromocoes.findIndex(
                        (p) => p === item
                      );

                      return (
                        <Badge
                          key={index}
                          text={textColor}
                          className="d-flex align-items-center py-1 px-2 border bg-body"
                          style={{ borderRadius: "16px" }}
                        >
                          <span className="me-1 text-primary">
                            {item.descricao}
                          </span>
                          <Button
                            variant="link"
                            className="p-0 ms-1 text-primary border-0"
                            onClick={() => handleRemoverItem(originalIndex)}
                            aria-label="Remover item"
                            style={{ fontSize: "12px" }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </Badge>
                      );
                    })}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          <LojasAssociadasPromocao
            selectedLojas={empresasAssociadas}
            onToggleLoja={handleToggleLoja}
          />

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                if (isDirtyRef.current) {
                  setPendingNavigation("/cadastros/promocoes");
                  setShowNavigationModal(true);
                } else {
                  navigate("/cadastros/promocoes");
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar Alterações
            </Button>
          </div>
        </Form>
      </FormProvider>
      <SelecaoItensOffcanvas
        show={showModalItens}
        onHide={() => setShowModalItens(false)}
        onConfirm={(selectedItems) => {
          form.setValue("produtoPromocoes", selectedItems, {
            shouldDirty: true,
          });
        }}
        selectedItems={produtoPromocoes}
        aplicarPara={
          aplicarPara === 1
            ? "PRODUTO"
            : aplicarPara === 2
            ? "DEPARTAMENTO"
            : "CATEGORIA"
        }
      />
    </div>
  );
};

export default EditarPromocao;
